import {
	BaseQueryFn,
	createApi,
	FetchArgs,
	fetchBaseQuery,
	FetchBaseQueryArgs,
	FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";
import LocalStorageService from "@/services/LocalStorage/LocalStorageService";
import { authActions } from "@/store/auth/authSlice";
import {
	LSKeyAccessToken,
	LSKeyAccessTokenExp,
	LSKeyRefreshToken,
} from "@/lib/LocalStorageConstants";

const baseApiUrl =
	process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const mutex = new Mutex();

/**
 * Converts expiresIn to an absolute Unix timestamp (seconds).
 * If the value looks like a relative duration (< 1 billion), it's treated
 * as "seconds from now". Otherwise it's already an absolute timestamp.
 */
const toAbsoluteExp = (expiresIn: number | undefined): number => {
	if (!expiresIn) return Math.floor(Date.now() / 1000) + 3600;
	if (expiresIn < 1_000_000_000) return Math.floor(Date.now() / 1000) + expiresIn;
	return expiresIn;
};

const baseQueryFn = (url: string | undefined) => {
	const baseQueryArgs: FetchBaseQueryArgs = {
		baseUrl: url,
		prepareHeaders(headers: any, { endpoint }: any) {
			const orgKey = process.env.REACT_APP_ORGANIZATION_ID;
			if (orgKey) {
				headers.set("X-Org-Key", orgKey);
			}
			if (endpoint === "login" || endpoint === "refresh" || endpoint === "register" || endpoint === "googleAuth") {
				return headers;
			}
			const accessToken = LocalStorageService.get(LSKeyAccessToken);
			if (accessToken) {
				headers.set("Authorization", "Bearer " + accessToken);
			}
			return headers;
		},
	};

	const baseQuery = fetchBaseQuery(baseQueryArgs);

	const NO_REAUTH_ENDPOINTS = new Set(["login", "register", "refresh", "googleAuth", "changePassword"]);

	const baseQueryWithReauth: BaseQueryFn<
		string | FetchArgs,
		unknown,
		FetchBaseQueryError
	> = async (args, api, extraOptions) => {
		await mutex.waitForUnlock();
		let result = await baseQuery(args, api, extraOptions);

		if (
			result.error &&
			(result.error.status === 401 ||
				(result.error.data as any)?.status_code === 401) &&
			!NO_REAUTH_ENDPOINTS.has(api.endpoint)
		) {
			if (!mutex.isLocked()) {
				const release = await mutex.acquire();

				try {
					const refreshToken =
						LocalStorageService.get(LSKeyRefreshToken);

					const refreshResult = await fetchBaseQuery({
						...baseQueryArgs,
						baseUrl: baseApiUrl,
					})(
						{
							url: "/auth/refresh-token",
							method: "POST",
							body: { refresh_token: refreshToken },
						},
						api,
						extraOptions,
					);

					const data = refreshResult.data as any;
					const newAccessToken = data?.accessToken ?? data?.access_token;
					const newRefreshToken = data?.refreshToken ?? data?.refresh_token;
					const newExpiresIn = data?.expiresIn ?? data?.expires_in;

					if (newAccessToken) {
						LocalStorageService.set(LSKeyAccessToken, newAccessToken);
						LocalStorageService.set(LSKeyAccessTokenExp, toAbsoluteExp(newExpiresIn));
						LocalStorageService.set(LSKeyRefreshToken, newRefreshToken);

						result = await baseQuery(args, api, extraOptions);
					} else {
						LocalStorageService.remove(LSKeyAccessToken);
						LocalStorageService.remove(LSKeyRefreshToken);
						LocalStorageService.remove(LSKeyAccessTokenExp);

						api.dispatch(authActions.setAuth(false));
						api.dispatch(mainBackendApi.util.resetApiState());
					}
				} finally {
					release();
				}
			} else {
				await mutex.waitForUnlock();
				if (!LocalStorageService.get(LSKeyAccessToken)) {
					return result;
				}
				result = await baseQuery(args, api, extraOptions);
			}
		}

		return result;
	};

	return baseQueryWithReauth;
};

export const mainBackendApi = createApi({
	reducerPath: "mainBackendApi",
	baseQuery: baseQueryFn(baseApiUrl),
	endpoints: () => ({}),
	tagTypes: ["User", "Balance", "Card", "DepositAddress", "Activity", "CardProduct", "NetworkCurrency", "Kyc"],
});
